import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { PermissionsService } from '@shared-services/permissions.service';
import { map } from 'rxjs';

export interface PermissionGuardOptions {
  /** Pass when the person has AT LEAST ONE key (default: all of them). */
  any?: boolean;
  /** Where to send people without access (default: the 403 screen). */
  redirectTo?: string;
}

interface PermissionRequirement {
  list: readonly string[];
  options: PermissionGuardOptions;
}

/** What each guard created by `permissionGuard` asks for. */
const requirements = new WeakMap<CanActivateFn, PermissionRequirement>();

const isAllowed = (
  permissions: PermissionsService,
  { list, options }: PermissionRequirement,
): boolean => (options.any ? permissions.canAny(list) : permissions.can(list));

/**
 * Route gating by permission. Without access it re-reads `/me` once and, if
 * still denied, redirects to the 403 screen (`/forbidden`) instead of failing
 * silently; the dashboard sends people to "Inicio" instead.
 */
export function permissionGuard(
  keys: string | readonly string[],
  options: PermissionGuardOptions = {},
): CanActivateFn {
  const requirement: PermissionRequirement = {
    list: typeof keys === 'string' ? [keys] : keys,
    options,
  };
  const guard: CanActivateFn = () => {
    const permissions = inject(PermissionsService);
    const router = inject(Router);
    const allowed = () => isAllowed(permissions, requirement);
    if (allowed()) return true;
    // The stored session may predate an access change (e.g. right after a
    // reload): re-read `/me` before denying.
    return permissions
      .refresh()
      .pipe(
        map(() =>
          allowed()
            ? true
            : router.createUrlTree([options.redirectTo ?? '/forbidden']),
        ),
      );
  };
  requirements.set(guard, requirement);
  return guard;
}

/**
 * Re-checks the permission guards of the current route against the session
 * as it is now. Guards only run on navigation, so after an access change the
 * person could stay on a screen they can no longer use. Returns where to send
 * them, or null when the route is still allowed.
 */
export function deniedRedirect(
  router: Router,
  permissions: PermissionsService,
): UrlTree | null {
  for (
    let route = router.routerState.snapshot.root.firstChild;
    route;
    route = route.firstChild
  ) {
    for (const guard of route.routeConfig?.canActivate ?? []) {
      const requirement = requirements.get(guard as CanActivateFn);
      if (requirement && !isAllowed(permissions, requirement)) {
        return router.createUrlTree([
          requirement.options.redirectTo ?? '/forbidden',
        ]);
      }
    }
  }
  return null;
}

/** `dashboard:view` or else the simple "Inicio" screen. */
export const dashboardGuard: CanActivateFn = permissionGuard('dashboard:view', {
  redirectTo: '/home',
});

/** Root and unknown URLs: the landing of the session (`landing.target`). */
export const landingRedirectGuard: CanActivateFn = () =>
  inject(Router).createUrlTree([inject(PermissionsService).landingUrl]);
